import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  input,
  output,
  signal,
  viewChildren,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

const DIGIT_COUNT = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_CONFIRMATION_SECONDS = 3;
const FULL_CODE_PATTERN = new RegExp(`^\\d{${DIGIT_COUNT}}$`);

@Component({
  selector: 'app-otp-dialog',
  standalone: true,
  imports: [A11yModule, MatIcon, TranslatePipe],
  templateUrl: './otp-dialog.component.html',
  styleUrl: './otp-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpDialogComponent implements OnInit, OnDestroy {
  readonly email = input.required<string>();
  readonly submitting = input(false);
  readonly errorMessage = input<string | null>(null);
  readonly allowEmailChange = input(true);

  readonly verify = output<string>();
  readonly resend = output<void>();
  readonly changeEmail = output<void>();
  readonly dismiss = output<void>();

  protected readonly digits = signal<string[]>(Array(DIGIT_COUNT).fill(''));
  protected readonly code = computed(() => this.digits().join(''));
  protected readonly canSubmit = computed(() => this.code().length === DIGIT_COUNT && !this.submitting());

  protected readonly cooldownSeconds = signal(RESEND_COOLDOWN_SECONDS);
  protected readonly justResent = signal(false);
  protected readonly resendDisabled = computed(() => this.cooldownSeconds() > 0);

  private readonly digitInputs = viewChildren<ElementRef<HTMLInputElement>>('digitInput');
  private cooldownIntervalId?: ReturnType<typeof setInterval>;
  private confirmationTimeoutId?: ReturnType<typeof setTimeout>;

  constructor() {
    let wasSubmitting = false;
    let previousError: string | null = null;
    effect(() => {
      const submitting = this.submitting();
      const error = this.errorMessage();
      // Two triggers, since the backend returns the same generic "invalid code" text for every
      // failed attempt and a signal write with an unchanged value does not re-run effects that
      // only read it: the error text actually changing, or a submit attempt just completing
      // while an (unchanged) error is present.
      const errorChanged = !!error && error !== previousError;
      const attemptJustFailed = wasSubmitting && !submitting && !!error;
      if (errorChanged || attemptJustFailed) {
        this.digits.set(Array(DIGIT_COUNT).fill(''));
        this.focusDigit(0);
      }
      wasSubmitting = submitting;
      previousError = error;
    });
  }

  ngOnInit(): void {
    this.startCooldown();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  onDigitInput(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const digit = value.replace(/\D/g, '').slice(-1);

    this.digits.update((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });
    (event.target as HTMLInputElement).value = digit;

    if (digit && index < DIGIT_COUNT - 1) {
      this.focusDigit(index + 1);
    }
  }

  onDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key !== 'Backspace') {
      return;
    }
    const isEmpty = this.digits()[index] === '';
    if (isEmpty && index > 0) {
      event.preventDefault();
      this.digits.update((current) => {
        const next = [...current];
        next[index - 1] = '';
        return next;
      });
      this.focusDigit(index - 1);
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pasted = (event.clipboardData?.getData('text') ?? '').trim();
    if (!FULL_CODE_PATTERN.test(pasted)) {
      return;
    }
    event.preventDefault();
    this.digits.set(pasted.split(''));
    this.focusDigit(DIGIT_COUNT - 1);
  }

  onSubmit(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.verify.emit(this.code());
  }

  onResendClick(): void {
    if (this.resendDisabled()) {
      return;
    }
    this.resend.emit();
    this.justResent.set(true);
    this.startCooldown();
    this.confirmationTimeoutId = setTimeout(() => this.justResent.set(false), RESEND_CONFIRMATION_SECONDS * 1000);
  }

  onChangeEmailClick(): void {
    this.changeEmail.emit();
  }

  // Separate from changeEmail: escape has to stay a way out of the focus trap even for callers that
  // do not offer an email step, so they decide themselves where it leads.
  onEscape(): void {
    this.dismiss.emit();
  }

  private startCooldown(): void {
    this.clearTimers();
    this.cooldownSeconds.set(RESEND_COOLDOWN_SECONDS);
    this.cooldownIntervalId = setInterval(() => {
      const next = this.cooldownSeconds() - 1;
      this.cooldownSeconds.set(Math.max(next, 0));
      if (next <= 0 && this.cooldownIntervalId) {
        clearInterval(this.cooldownIntervalId);
      }
    }, 1000);
  }

  private clearTimers(): void {
    if (this.cooldownIntervalId) {
      clearInterval(this.cooldownIntervalId);
      this.cooldownIntervalId = undefined;
    }
    if (this.confirmationTimeoutId) {
      clearTimeout(this.confirmationTimeoutId);
      this.confirmationTimeoutId = undefined;
    }
  }

  private focusDigit(index: number): void {
    this.digitInputs()[index]?.nativeElement.focus();
  }
}
